/* @jsxImportSource @emotion/react */
/* eslint-disable formatjs/no-literal-string-in-jsx */

import { css } from '@emotion/react'
import classNames from 'classnames'

import { CustomNoteIcon } from '@slite/components'
import {
  ArrowUpIcon,
  CaretNavigationRightIcon,
  ChevronIcon,
  CreateNoteIcon,
} from '@slite/icons'

import { TrafficDots } from './TrafficDots'

const SIDEBAR_WIDTH = 200
const MAIN_MIN_WIDTH = 210

const BORDER_COLOR = '#ECECEA'
const TEXT_DARK = '#21201E'
const TEXT_MID = '#494743'
const TEXT_FAINT = '#83817D'

type TreeRow = {
  title: string
  icon: { shape: string; color: string }
  depth?: number
  isExpanded?: boolean
}

const TREE: TreeRow[] = [
  {
    title: 'Company Wiki',
    icon: { shape: 'briefcase', color: 'orange' },
    isExpanded: true,
  },
  {
    title: 'About us',
    icon: { shape: 'white_check_mark', color: 'green' },
    depth: 1,
  },
  {
    title: 'Remote Handbook',
    icon: { shape: 'world_map', color: 'purple' },
    depth: 1,
  },
  {
    title: 'HR Policies',
    icon: { shape: 'busts_in_silhouette', color: 'pink' },
    depth: 1,
  },
  {
    title: 'Performance Review',
    icon: { shape: 'gear', color: 'green' },
    depth: 1,
  },
  {
    title: 'Product',
    icon: { shape: 'rocket', color: 'pink' },
  },
  {
    title: 'The Slite Help Center',
    icon: { shape: 'department_store', color: 'blue' },
  },
]

const rootCss = css`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #F7F7F5;
  font-size: 13px;
  color: ${TEXT_DARK};
  border: 4px solid #F0F0F0;
  border-radius: 16px;
  overflow: hidden;
  background: #F7F7F5;
`

const treeRowCss = css`
  display: flex;
  align-items: center;
  gap: 7px;
  height: 28px;
  padding: 6px;
  white-space: nowrap;
`

export function SliteInterface() {
  return (
    <div css={rootCss} aria-hidden>
      <div
        className="flexbox flexbox-align-center flexbox-no-shrink"
        style={{
          gap: 12,
          height: 40,
          padding: '0 16px',
          overflow: 'hidden',
        }}
      >
        <TrafficDots />
        <span className="font-weight-semibold" style={{ color: '#1A1A18' }}>
          Slite
        </span>
      </div>
      <div className="flexbox flexbox-grow" style={{ minHeight: 0 }}>
        <div
          className="flexbox flexbox-column flexbox-no-shrink"
          style={{
            width: SIDEBAR_WIDTH,
            padding: 12,
            background: '#F7F7F5',
          }}
        >
          <div
            className="flexbox flexbox-align-center flexbox-space-between"
            style={{ margin: '2px 6px 12px' }}
          >
            <span style={{ fontSize: 13, color: TEXT_FAINT }}>Channels</span>
            <CreateNoteIcon size={15} style={{ color: TEXT_FAINT }} />
          </div>
          {TREE.map((row) => (
            <div
              key={row.title}
              css={treeRowCss}
              style={{ marginLeft: (row.depth ?? 0) * 22 }}
            >
              <CaretNavigationRightIcon
                size={12}
                className={classNames('flexbox-no-shrink', {
                  'rotate-90': row.isExpanded,
                })}
                style={{ color: '#A9A7A3' }}
              />
              <CustomNoteIcon
                size={16}
                iconShape={row.icon.shape}
                iconColor={row.icon.color}
                expanded={row.isExpanded}
              />
              <span className="ellipsis">{row.title}</span>
            </div>
          ))}
        </div>
        <div
          className="flexbox flexbox-column flexbox-grow"
          style={{
            minWidth: MAIN_MIN_WIDTH,
            background: 'var(--white)',
            borderRadius: '12px 0 0 0',
            border: `1px solid ${BORDER_COLOR}`,
            borderBottom: 'none',
            overflow: 'hidden',
          }}
        >
          <div
            className="flexbox flexbox-align-center flexbox-no-shrink"
            style={{
              gap: 12,
              height: 30,
              margin: '8px 0 0',
              padding: '0 16px',
            }}
          >
            <ChevronIcon
              size={14}
              className="rotate-180"
              style={{ color: TEXT_DARK }}
            />
            <ChevronIcon size={14} style={{ color: '#C4C2BF' }} />
            <span style={{ marginLeft: 4, marginTop: 1, color: TEXT_MID }}>
              Slite Agent
            </span>
          </div>
          <div className="flexbox flexbox-centered flexbox-grow"></div>
          <div style={{ padding: 14, paddingRight: 24, marginBottom: 8 }}>
            <div
              className="flexbox flexbox-align-center flexbox-space-between"
              style={{
                height: 48,
                padding: '0 8px 0 14px',
                borderRadius: 12,
                border: '1px solid #E9E9E7',
                background: 'var(--white)',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
              }}
            >
              <span style={{ color: '#A5A3A0' }}>Ask Slite Agent...</span>
              <span
                className="flexbox flexbox-centered flexbox-no-shrink"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: '#191919',
                }}
              >
                <ArrowUpIcon size={16} style={{ color: 'var(--white)' }} />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
